import { TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD } from 'constants/tours'
import { dateDifference } from 'lib/datetime/dateUtils'
import isTourV2DepositAllowed from 'lib/tours/isTourV2DepositAllowed'
import getMatchingTourPaymentScheduleTemplate from './getMatchingTourPaymentScheduleTemplate'

const DEFAULT_DEPOSIT_PERCENTAGE = 25

export type TourV2DepartureDeposit =
  | { source: 'payment-schedule', deposit: App.Tours.TourPaymentSchedule, availableFromDays: number }
  | { source: 'tour-deposit', deposit: App.Tours.TourPaymentSchedule, balanceDueDays: number }

interface Options {
  offer: Pick<
    App.Tours.TourV2Offer,
    | 'depositType'
    | 'depositAmount'
    | 'depositThresholds'
    | 'paymentScheduleTemplate'
    | 'paymentScheduleTemplates'
  >
  departureStartDate: string
  pricePerPerson: number | undefined
  currencyCode: string
  defaultDepositPercentage?: number
  arePaymentSchedulesEnabled?: boolean
}

function getOfferTemplates(offer: Options['offer']) {
  if (offer.paymentScheduleTemplates?.length) return offer.paymentScheduleTemplates
  return offer.paymentScheduleTemplate ? [offer.paymentScheduleTemplate] : undefined
}

/**
 * Offer-level "Available when booking at least X days" floor.
 * Lead-time sets use earliest band minDays (typically 119); a lone template uses minimumLeadDays.
 */
export function getEarliestPaymentScheduleAvailability(offer: Options['offer']): number | undefined {
  const templates = getOfferTemplates(offer)
  if (!templates?.length) return undefined
  if (templates.length === 1) return templates[0].minimumLeadDays
  return Math.min(...templates.map(t => t.leadTimeRange?.minDays ?? t.minimumLeadDays))
}

function getTourDepositSchedule(
  offer: Options['offer'],
  defaultDepositPercentage: number | undefined,
): App.Tours.TourPaymentSchedule | undefined {
  const depositType = offer.depositType ?? 'default'

  if (depositType === 'flat') {
    const amountValue = offer.depositAmount ?? 0
    return amountValue > 0 ? { amountType: 'fixed_amount', amountValue } : undefined
  }

  const percentage = depositType === 'percentage'
    ? offer.depositAmount ?? 0
    : defaultDepositPercentage ?? DEFAULT_DEPOSIT_PERCENTAGE

  return percentage > 0 ? { amountType: 'percentage', amountValue: percentage / 100 } : undefined
}

/** Payment schedule match first; otherwise the tour's own deposit config. */
export default function getTourV2DepartureDeposit({
  offer,
  departureStartDate,
  pricePerPerson,
  currencyCode,
  defaultDepositPercentage,
  arePaymentSchedulesEnabled = true,
}: Options): TourV2DepartureDeposit | undefined {
  if (arePaymentSchedulesEnabled) {
    const daysTillDeparture = dateDifference(new Date(departureStartDate)).days
    const templates = getOfferTemplates(offer)
    const matchedTemplate = getMatchingTourPaymentScheduleTemplate(templates, daysTillDeparture)
    const scheduleDeposit = matchedTemplate?.schedules[0]
    if (matchedTemplate && scheduleDeposit) {
      return {
        source: 'payment-schedule',
        deposit: scheduleDeposit,
        availableFromDays: getEarliestPaymentScheduleAvailability(offer) ?? matchedTemplate.minimumLeadDays,
      }
    }
  }

  const depositType = offer.depositType ?? 'default'
  if (!pricePerPerson || !isTourV2DepositAllowed(
    departureStartDate,
    pricePerPerson,
    currencyCode,
    depositType,
    offer.depositThresholds?.numberOfDays,
  )) {
    return undefined
  }

  const deposit = getTourDepositSchedule(offer, defaultDepositPercentage)
  if (!deposit) return undefined

  return {
    source: 'tour-deposit',
    deposit,
    balanceDueDays: offer.depositThresholds?.numberOfDays ?? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD,
  }
}

type OfferSummaryOptions = Omit<Options, 'offer' | 'departureStartDate' | 'pricePerPerson'> & {
  offer: Options['offer'] & Pick<App.Tours.TourV2Offer, 'departures'>
}

/**
 * Offer-level deposit, applying the same precedence as a single departure lifted across
 * the offer: a payment schedule on any departure wins, otherwise the tour deposit,
 * otherwise nothing. Within each source the soonest available departure is used.
 */
export function getTourV2OfferDepositSummary({
  offer,
  currencyCode,
  defaultDepositPercentage,
  arePaymentSchedulesEnabled = true,
}: OfferSummaryOptions): TourV2DepartureDeposit | undefined {
  const availableDepartures = Object.values(offer.departures)
    .filter(departure => departure.status === 'available')
    .sort((a, b) => a.startDate.localeCompare(b.startDate))

  let tourDeposit: TourV2DepartureDeposit | undefined

  for (const departure of availableDepartures) {
    const prices = departure.options.map(option => option.price)
    const resolved = getTourV2DepartureDeposit({
      offer,
      departureStartDate: departure.startDate,
      pricePerPerson: prices.length ? Math.min(...prices) : undefined,
      currencyCode,
      defaultDepositPercentage,
      arePaymentSchedulesEnabled,
    })

    if (resolved?.source === 'payment-schedule') return resolved
    tourDeposit ??= resolved
  }

  return tourDeposit
}
